'use client';

import { differenceInDays } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import useSwrInfinite from 'swr/infinite';
import { DropdownCheckbox } from '@/app_components/Conferences/DropdownCheckbox';
import { EventsLoader } from '@/app_components/Conferences/EventsLoader';
import { EventTile } from '@/app_components/Conferences/EventTile';
import { Toggle } from '@/app_components/Conferences/Toggle';
import { Icon } from '@/app_components/common/Icon';
import { SpinningLoader } from '@/app_components/icons/SpinningLoader';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import { Breakpoints, useMediaQuery } from '@/app_lib/hooks/useMediaQuery';
import type { ConferencesHomepage, EventsFilters, EventsListEvent } from '@/app_lib/types/Conferences';
import type { OptionWithChecked } from '@/app_lib/utils/dropdownCheckboxUtils';
import { cn } from '@/app_lib/utils/utils';

function fetcher(url: string): Promise<{ events: EventsListEvent[]; totalResults: number }> {
  return fetch(url).then((res) => res.json());
}

function buildRegionSelection(filters: OptionWithChecked[]) {
  const selectedContinents = filters
    .filter((option) => option.checked && option.value && option.value !== 'virtual')
    .map((option) => option.value);
  const selectedCities = filters
    .filter((option) => !option.checked && Array.isArray(option.children))
    .map((option) => option.children?.flatMap((child) => child.options.filter((c) => c.checked).map((c) => c.value)));
  const selectedVirtual = filters
    .filter((option) => option.checked && option.value === 'virtual')
    .map((option) => option.value);
  return { cities: selectedCities.flat(), continents: selectedContinents, virtual: selectedVirtual.length > 0 };
}

function buildRegionFilters(filters: EventsFilters) {
  const virtualFilter = filters.virtual ? { label: 'Virtual', value: 'virtual' } : null;
  return filters.continents
    .map((continent) => {
      if (continent === 'North America') {
        const usCities = filters.usCities.map((city) => ({ label: city, value: city.slice(0, -4) }));
        usCities.sort((a, b) => a.label.localeCompare(b.label));
        return {
          children: [
            {
              options: usCities,
              title: 'United States',
            },
          ],
          label: 'Americas',
          value: continent,
        };
      }

      return { label: continent, value: continent };
    })
    .sort((a, b) => a.label.localeCompare(b.label))
    .concat(virtualFilter ? [{ label: 'Virtual', value: 'virtual' }] : []);
}

function getSeeMoreText(events: EventsListEvent[], totalResults: number) {
  if (events.length > 1) {
    return `1-${events.length} of ${totalResults} events`;
  }
  if (events.length < totalResults) {
    return `1 of ${totalResults} events`;
  }
  return '1 event';
}

function isEmptyFilters(filters) {
  return (
    filters.continents.length === 0 &&
    filters.cities.length === 0 &&
    filters.topics.length === 0 &&
    filters.virtual === false
  );
}

const ConferencesEventsConfig = {
  [Breakpoints.Mobile]: 4,
  [Breakpoints.Tablet]: 9,
  [Breakpoints.Desktop]: 9,
};

export const EventSection = ({
  className,
  events: initialEvents,
  totalResults: initialTotalResults,
  filters: allFilters,
}: {
  className?: string;
  events: EventsListEvent[];
  totalResults: number;
  filters: ConferencesHomepage['filters'];
}) => {
  const [value, setValue] = useState<'upcoming' | 'past'>('upcoming');
  const [upcomingResetTrigger, setUpcomingResetTrigger] = useState(0);
  const [pastResetTrigger, setPastResetTrigger] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState({
    past: {
      cities: [],
      continents: [],
      topics: [],
      virtual: false,
    },
    upcoming: {
      cities: [],
      continents: [],
      topics: [],
      virtual: false,
    },
  });

  const isMobile = useMediaQuery(Breakpoints.Mobile);

  let eventsPerPage = ConferencesEventsConfig[Breakpoints.Desktop];
  if (isMobile && value === 'past') {
    eventsPerPage = ConferencesEventsConfig[Breakpoints.Mobile];
  }

  // Used for SWR infinite loading - fetches based on offset and limit (4 on mobile, 9 on desktop)
  const getKey = (pageIndex: number, previousPageData: { events: EventsListEvent[]; totalResults: number } | null) => {
    if (previousPageData && previousPageData.events.length < eventsPerPage) {
      return null;
    }
    const limit = eventsPerPage;
    const offset = pageIndex * eventsPerPage;
    const past = value === 'past';
    const continents = selectedFilters[value].continents.join(',');
    const cities = selectedFilters[value].cities.join(',');
    const topics = selectedFilters[value].topics.join(',');

    const params = new URLSearchParams({
      cities,
      continents,
      limit: String(limit),
      offset: String(offset),
      past: String(past),
      topics,
    });
    if (selectedFilters[value].virtual) {
      params.set('virtual', 'true');
    }
    return `/api/conferences/getEvents?${params.toString()}`;
  };

  const { data, isLoading, size, setSize, isValidating } = useSwrInfinite(getKey, fetcher, {
    // Only use server's 9 events as fallback on desktop; on mobile we refetch with limit=0
    fallbackData:
      isEmptyFilters(selectedFilters.upcoming) && value === 'upcoming'
        ? [{ events: initialEvents, totalResults: initialTotalResults }]
        : undefined,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnMount: false,
  });

  const events: EventsListEvent[] = data?.flatMap((page) => page.events) ?? initialEvents;
  const totalResults = data?.[0]?.totalResults ?? initialTotalResults;

  const showSeeMore = events.length < totalResults;
  const seeMoreText = getSeeMoreText(events, totalResults);

  const upcomingRegionFilters = useMemo(() => buildRegionFilters(allFilters.upcoming), [allFilters.upcoming]);
  const pastRegionFilters = useMemo(() => buildRegionFilters(allFilters.past), [allFilters.past]);
  const upcomingTopicOptions = useMemo(
    () => allFilters.upcoming.topics.map((topic) => ({ label: topic, value: topic })),
    [allFilters.upcoming.topics],
  );
  const pastTopicOptions = useMemo(
    () => allFilters.past.topics.map((topic) => ({ label: topic, value: topic })),
    [allFilters.past.topics],
  );

  const handleFilterChange = useCallback(
    (filter: 'regions' | 'topics', time: 'upcoming' | 'past', newOptions: OptionWithChecked[]) => {
      switch (filter) {
        case 'regions': {
          const newSelectedRegions = buildRegionSelection(newOptions);
          setSelectedFilters((prev) => ({
            ...prev,
            [time]: {
              ...prev[time],
              ...newSelectedRegions,
            },
          }));
          break;
        }
        default: {
          setSelectedFilters((prev) => ({
            ...prev,
            [time]: {
              ...prev[time],
              topics: newOptions.flatMap((option) => option.checked && option.value).filter(Boolean),
            },
          }));
          break;
        }
      }
      // Reset to first page on filter change
      setSize(1);
    },
    [setSize],
  );

  const handleClearFilters = useCallback(
    (time: 'upcoming' | 'past') => {
      handleFilterChange('regions', time, []);
      handleFilterChange('topics', time, []);
      time === 'upcoming' ? setUpcomingResetTrigger((t) => t + 1) : setPastResetTrigger((t) => t + 1);
    },
    [handleFilterChange],
  );

  return (
    <div
      className={cn('flex flex-col layout-gap-xs w-full scroll-mt-22', className)}
      data-cy='events-section'
      id='events'
    >
      <h2 className='typography-level-2 font-semibold'>Events</h2>
      <div className='flex flex-col md:flex-row md:justify-between items-end layout-gap-xs w-full'>
        <Toggle<'upcoming' | 'past'>
          className='w-full md:w-[238px]'
          data-cy='events-toggle'
          disabled={isLoading}
          setValue={setValue}
          value={value}
          values={['upcoming', 'past']}
        />
        <div className={cn('flex gap-lg md:gap-sm max-md:justify-between max-md:w-full', value === 'past' && 'hidden')}>
          <ButtonV2
            as='button'
            className={cn('typography-sm', isEmptyFilters(selectedFilters.upcoming) && 'invisible')}
            data-cy='clear-upcoming-filters-button'
            isTertiary
            onClick={() => handleClearFilters('upcoming')}
          >
            <span>Clear</span>
          </ButtonV2>
          <div className='flex gap-lg md:gap-sm'>
            <DropdownCheckbox
              data-cy='events-upcoming-region-filter'
              onFilterChange={(filters) => handleFilterChange('regions', 'upcoming', filters)}
              options={upcomingRegionFilters}
              resetTrigger={upcomingResetTrigger}
              title='Region'
            />
            <DropdownCheckbox
              data-cy='events-upcoming-topic-filter'
              onFilterChange={(filters) => handleFilterChange('topics', 'upcoming', filters)}
              options={upcomingTopicOptions}
              resetTrigger={upcomingResetTrigger}
              title='Topic'
            />
          </div>
        </div>
        <div
          className={cn('flex gap-lg md:gap-sm max-md:justify-between max-md:w-full', value === 'upcoming' && 'hidden')}
        >
          <ButtonV2
            as='button'
            className={cn('typography-sm', isEmptyFilters(selectedFilters.past) && 'invisible')}
            data-cy='clear-past-filters-button'
            isTertiary
            onClick={() => handleClearFilters('past')}
          >
            <span>Clear</span>
          </ButtonV2>
          <div className='flex gap-lg md:gap-sm'>
            <DropdownCheckbox
              data-cy='events-past-region-filter'
              onFilterChange={(filters) => handleFilterChange('regions', 'past', filters)}
              options={pastRegionFilters}
              resetTrigger={pastResetTrigger}
              title='Region'
            />
            <DropdownCheckbox
              data-cy='events-past-topic-filter'
              onFilterChange={(filters) => handleFilterChange('topics', 'past', filters)}
              options={pastTopicOptions}
              resetTrigger={pastResetTrigger}
              title='Topic'
            />
          </div>
        </div>
      </div>

      <div
        className='flex flex-col layout-gap-default'
        data-cy='events-cards'
      >
        <div
          className='flex flex-col md:grid grid-flow-row grid-cols-[repeat(2,1fr)] lg:grid-cols-[repeat(3,1fr)] layout-grid-gap lg:max-xl:gap-4'
          id={`${value}-events`}
        >
          {!isLoading &&
            events.map((event) => {
              const daysAway = differenceInDays(new Date(event.startDate), new Date());
              const isPast = daysAway < 0;

              return (
                <EventTile
                  event={event}
                  isPast={isPast}
                  key={event.id}
                />
              );
            })}
          {isLoading && <EventsLoader />}
          {!isLoading && events.length === 0 && (
            <div
              className='col-span-full flex flex-col items-center justify-center h-full w-full max-lg:aspect-square xl:max-h-[450px] lg:aspect-[12/5] md:aspect-[20/11] layout-gap-xs'
              id='no-events'
            >
              <div className='flex flex-col gap-sm items-center justify-center text-center'>
                <div className='relative'>
                  <Icon
                    className='text-blue-1'
                    icon='Calendar'
                    size='xl'
                  />
                </div>
                <span className='typography-level-2 font-semibold'>No events match your filters.</span>
                <span className='text-secondary'>Try another topic or region.</span>
              </div>
              <ButtonV2
                as='button'
                className='typography-sm'
                noCapitalize
                onClick={() => handleClearFilters(value)}
                variant='secondaryOutline'
              >
                <span>Clear filters</span>
              </ButtonV2>
            </div>
          )}
        </div>

        {/* We want to show all events on mobile if its upcoming. */}
        {totalResults > ConferencesEventsConfig[Breakpoints.Desktop] && value === 'upcoming' && (
          <div className='flex md:hidden flex-col max-w-[230px] h-[84px] items-center justify-center self-center gap-lg'>
            <SpinningLoader size={16} />
          </div>
        )}

        {/* See More button, hidden until the first fetch (on mobile) is complete */}
        {showSeeMore && (
          <div className='flex flex-col max-w-[230px] h-[84px] items-center justify-center self-center gap-lg'>
            <span className='typography-sm text-secondary'>{seeMoreText}</span>
            <ButtonV2
              as='button'
              disabled={isValidating}
              noCapitalize
              onClick={() => setSize(size + 1)}
              size='lg'
              variant='secondaryOutline'
            >
              <span>See more events</span>
              {isValidating ? (
                <SpinningLoader size={16} />
              ) : (
                <Icon
                  icon='ButtonDown'
                  size='custom'
                />
              )}
            </ButtonV2>
          </div>
        )}
      </div>
    </div>
  );
};
